import { useState, useEffect } from 'react';
import { tumansiApi } from '../../services/tumansiApi';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Save, Receipt, Calendar, DollarSign } from 'lucide-react';

const EXPENSE_CATEGORIES = [
    { value: 'fuel', label: 'Fuel' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'office_supplies', label: 'Office Supplies' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'rent', label: 'Rent' },
    { value: 'salaries', label: 'Salaries' },
    { value: 'police', label: 'Police' },
    { value: 'county_officials', label: 'County Officials' },
    { value: 'other', label: 'Other' },
];

const inp = { width: '100%', padding: '10px 12px', borderRadius: 9, border: '1.5px solid #e2e8f0', fontSize: '0.9rem', fontFamily: 'Poppins, sans-serif', outline: 'none', boxSizing: 'border-box' };

function Field({ label, children }) {
    return (
        <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                {label}
            </label>
            {children}
        </div>
    );
}

function Modal({ title, onClose, children }) {
    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }}>
            <div style={{
                background: '#fff', borderRadius: 18, width: '100%', maxWidth: 520,
                boxShadow: '0 20px 60px rgba(0,0,0,.2)', overflow: 'hidden',
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0',
                }}>
                    <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem', color: '#1e293b' }}>{title}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex' }}>
                        <X size={20} />
                    </button>
                </div>
                <div style={{ padding: '1.5rem' }}>{children}</div>
            </div>
        </div>
    );
}

export default function ExpenseTracker() {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // null | 'create' | { id, ...expense }
    const [saving, setSaving] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState('all');

    const [form, setForm] = useState({
        expense_date: new Date().toISOString().split('T')[0],
        category: 'fuel',
        amount_kes: '',
        description: '',
        payment_method: '',
        receipt_url: ''
    });

    useEffect(() => {
        loadExpenses();
    }, []);

    const loadExpenses = async () => {
        setLoading(true);
        try {
            const res = await tumansiApi.adminExpenses({ limit: 200 });
            setExpenses(res.data);
        } catch {
            toast.error('Failed to load expenses');
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setForm({
            expense_date: new Date().toISOString().split('T')[0],
            category: 'fuel',
            amount_kes: '',
            description: '',
            payment_method: '',
            receipt_url: ''
        });
        setModal('create');
    };

    const openEditModal = (expense) => {
        setForm({
            expense_date: expense.expense_date.split('T')[0],
            category: expense.category,
            amount_kes: expense.amount_kes,
            description: expense.description,
            payment_method: expense.payment_method || '',
            receipt_url: expense.receipt_url || ''
        });
        setModal(expense);
    };

    const handleSave = async () => {
        if (!form.amount_kes || !form.description) {
            toast.error('Amount and description are required');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                expense_date: new Date(form.expense_date).toISOString(),
                category: form.category,
                amount_kes: parseFloat(form.amount_kes),
                description: form.description,
                payment_method: form.payment_method || null,
                receipt_url: form.receipt_url || null
            };

            if (modal === 'create') {
                const res = await tumansiApi.adminCreateExpense(payload);
                setExpenses(prev => [res.data, ...prev]);
                toast.success('Expense created successfully');
            } else {
                const res = await tumansiApi.adminUpdateExpense(modal.id, payload);
                setExpenses(prev => prev.map(e => e.id === modal.id ? res.data : e));
                toast.success('Expense updated successfully');
            }
            setModal(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save expense');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Are you sure you want to delete this expense?')) return;

        try {
            await tumansiApi.adminDeleteExpense(id);
            setExpenses(prev => prev.filter(e => e.id !== id));
            toast.success('Expense deleted');
        } catch {
            toast.error('Failed to delete expense');
        }
    };

    const filteredExpenses = categoryFilter === 'all'
        ? expenses
        : expenses.filter(e => e.category === categoryFilter);

    const totalExpenses = filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount_kes), 0);

    const S = {
        btn: (color) => ({
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px',
            background: color, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer',
            fontSize: '0.8rem', fontWeight: 600, fontFamily: 'Poppins, sans-serif',
            whiteSpace: 'nowrap'
        }),
        th: { padding: '12px 10px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' },
        td: { padding: '14px 10px', fontSize: '0.85rem', color: '#334155', borderBottom: '1px solid #f1f5f9' },
        mobileCard: {
            background: '#fff',
            borderRadius: 12,
            padding: '1rem',
            border: '1px solid #e2e8f0',
            marginBottom: '0.75rem'
        }
    };

    if (loading) {
        return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading expenses...</div>;
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
                <div>
                    <h1 style={{ fontWeight: 800, fontSize: '1.4rem', color: '#1e293b', margin: 0 }}>💰 Expense Tracker</h1>
                    <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '4px 0 0' }}>
                        Total: <strong style={{ color: '#0f62a8' }}>KES {totalExpenses.toLocaleString()}</strong> ({filteredExpenses.length} expenses)
                    </p>
                </div>
                <button onClick={openCreateModal} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: '#0f62a8', color: '#fff', borderRadius: 10, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem', fontFamily: 'Poppins, sans-serif' }}>
                    <Plus size={15} /> Add Expense
                </button>
            </div>

            {/* Category Filter */}
            <div style={{ marginBottom: '1.5rem' }}>
                <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ ...inp, maxWidth: 250 }}>
                    <option value="all">All Categories</option>
                    {EXPENSE_CATEGORIES.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                </select>
            </div>

            {/* Mobile Card View */}
            <div className="mobile-only" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filteredExpenses.length === 0 ? (
                    <div style={{ background: '#fff', borderRadius: 14, padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                        No expenses found
                    </div>
                ) : (
                    filteredExpenses.map(expense => (
                        <div key={expense.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                                <div>
                                    <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>
                                        {EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}
                                    </span>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>
                                        {new Date(expense.expense_date).toLocaleDateString()}
                                    </div>
                                </div>
                                <div style={{ fontWeight: 800, color: '#dc2626', fontSize: '1.1rem' }}>
                                    KES {parseFloat(expense.amount_kes).toLocaleString()}
                                </div>
                            </div>
                            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem', fontSize: '0.85rem' }}>
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>Description:</span>
                                    <div style={{ color: '#334155', fontWeight: 500 }}>{expense.description}</div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f1f5f9' }}>
                                    <div>
                                        <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>Payment:</span>
                                        <div style={{ color: '#334155', fontSize: '0.8rem' }}>{expense.payment_method || '-'}</div>
                                    </div>
                                    <div>
                                        <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>Created By:</span>
                                        <div style={{ color: '#334155', fontSize: '0.8rem' }}>{expense.created_by_name || 'System'}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                                    <button style={{ ...S.btn('#0f62a8'), flex: 1 }} onClick={() => openEditModal(expense)}>
                                        <Edit2 size={12} /> Edit
                                    </button>
                                    <button style={{ ...S.btn('#dc2626'), flex: 1 }} onClick={() => handleDelete(expense.id)}>
                                        <Trash2 size={12} /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Desktop Table View */}
            <div className="desktop-only" style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={S.th}>Date</th>
                                <th style={S.th}>Category</th>
                                <th style={S.th}>Description</th>
                                <th style={S.th}>Amount (KES)</th>
                                <th style={S.th}>Payment Method</th>
                                <th style={S.th}>Created By</th>
                                <th style={S.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredExpenses.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ ...S.td, textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>
                                        No expenses found
                                    </td>
                                </tr>
                            ) : (
                                filteredExpenses.map(expense => (
                                    <tr key={expense.id}>
                                        <td style={S.td}>{new Date(expense.expense_date).toLocaleDateString()}</td>
                                        <td style={S.td}>
                                            <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600 }}>
                                                {EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}
                                            </span>
                                        </td>
                                        <td style={S.td}>{expense.description}</td>
                                        <td style={S.td}>
                                            <strong style={{ color: '#dc2626' }}>KES {parseFloat(expense.amount_kes).toLocaleString()}</strong>
                                        </td>
                                        <td style={S.td}>{expense.payment_method || '-'}</td>
                                        <td style={S.td}>{expense.created_by_name || 'System'}</td>
                                        <td style={S.td}>
                                            <div style={{ display: 'flex', gap: 5 }}>
                                                <button style={S.btn('#0f62a8')} onClick={() => openEditModal(expense)}>
                                                    <Edit2 size={12} /> Edit
                                                </button>
                                                <button style={S.btn('#dc2626')} onClick={() => handleDelete(expense.id)}>
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {modal && (
                <Modal title={modal === 'create' ? '➕ Add New Expense' : '✏️ Edit Expense'} onClose={() => setModal(null)}>
                    <Field label="Date">
                        <input type="date" style={inp} value={form.expense_date} onChange={e => setForm({ ...form, expense_date: e.target.value })} />
                    </Field>

                    <Field label="Category">
                        <select style={inp} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                            {EXPENSE_CATEGORIES.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Amount (KES)">
                        <input type="number" step="0.01" style={inp} value={form.amount_kes} onChange={e => setForm({ ...form, amount_kes: e.target.value })} placeholder="e.g. 5000.00" />
                    </Field>

                    <Field label="Description">
                        <textarea style={{ ...inp, minHeight: 80, resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe the expense..." />
                    </Field>

                    <Field label="Payment Method (Optional)">
                        <input style={inp} value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} placeholder="e.g. Cash, M-Pesa, Card" />
                    </Field>

                    <Field label="Receipt URL (Optional)">
                        <input style={inp} value={form.receipt_url} onChange={e => setForm({ ...form, receipt_url: e.target.value })} placeholder="https://..." />
                    </Field>

                    <button disabled={saving} onClick={handleSave} style={{ width: '100%', padding: '12px', background: '#0f62a8', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8 }}>
                        <Save size={15} /> {saving ? 'Saving...' : modal === 'create' ? 'Create Expense' : 'Update Expense'}
                    </button>
                </Modal>
            )}
        </div>
    );
}
